import { UserPOI } from '../../types/userPois'
import { getUserPOITypeById } from '../../data/userPoiTypes'
import { CheckCircle, XCircle } from 'lucide-react'

interface UserPOIMarkerProps {
  poi: UserPOI
  onClick?: (poi: UserPOI) => void
  isSelected?: boolean
}

export function UserPOIMarker({ poi, onClick, isSelected = false }: UserPOIMarkerProps) {
  const poiType = getUserPOITypeById(poi.type)
  if (!poiType) return null

  const Icon = poiType.icon
  const confirmationCount = Object.keys(poi.confirmations || {}).length
  const outdatedCount = Object.keys(poi.outdatedVotes || {}).length
  const totalVotes = confirmationCount + outdatedCount
  const confirmationRate = totalVotes > 0 ? confirmationCount / totalVotes : 0

  // Determine marker color based on type and confirmation status
  let bgColor = poiType.color
  let borderColor = poiType.color
  if (poi.resolved) {
    bgColor = '#374151' // gray-700 for resolved
    borderColor = '#6b7280' // gray-500
  } else if (totalVotes >= 3) {
    // If there are enough votes, adjust color based on confirmation rate
    if (confirmationRate >= 0.7) {
      // Keep original color for highly confirmed
    } else if (confirmationRate <= 0.3) {
      bgColor = '#374151' // gray-700 for outdated
      borderColor = '#ef4444' // red-500
    }
  }

  return (
    <div
      onClick={() => onClick?.(poi)}
      className={`
        relative cursor-pointer transition-all duration-200
        ${isSelected ? 'scale-110 z-20' : 'hover:scale-105 z-10'}
      `}
      style={{ 
        // The container needs proper dimensions for anchor to work
        display: 'inline-block'
      }}
    >
      {/* Tooltip-style marker with arrow at bottom */}
      <div className="relative" style={{ paddingBottom: '6px' }}>
        {/* Main tooltip body */}
        <div
          className={`
            p-1 rounded shadow-md backdrop-blur-sm
            ${poi.resolved ? 'bg-gray-800/90' : 'bg-gray-900/90'}
            border transition-all duration-200
            ${isSelected ? 'ring-1 ring-white ring-opacity-70' : ''}
          `}
          style={{ 
            borderColor: borderColor
          }}
        >
          <div className="flex items-center">
            <Icon className="w-3 h-3" style={{ color: borderColor }} />
            {/* Status indicator */}
            {totalVotes >= 3 && !poi.resolved && (
              <>
                {confirmationRate >= 0.7 ? (
                  <CheckCircle className="w-2 h-2 text-green-400 flex-shrink-0 ml-1" />
                ) : confirmationRate <= 0.3 ? (
                  <XCircle className="w-2 h-2 text-red-400 flex-shrink-0 ml-1" />
                ) : null}
              </>
            )}
            {poi.resolved && (
              <CheckCircle className="w-2 h-2 text-gray-400 flex-shrink-0 ml-1" />
            )}
          </div>
        </div>
        
        {/* Tooltip tail/arrow pointing down - positioned at absolute bottom */}
        <div 
          className="absolute left-1/2 transform -translate-x-1/2"
          style={{
            bottom: '2px',
            width: 0,
            height: 0,
            borderLeft: '3px solid transparent',
            borderRight: '3px solid transparent',
            borderTop: `4px solid ${borderColor}`,
          }}
        />
        <div 
          className="absolute left-1/2 transform -translate-x-1/2"
          style={{
            bottom: '2.5px',
            width: 0,
            height: 0,
            borderLeft: '2.5px solid transparent',
            borderRight: '2.5px solid transparent',
            borderTop: `3.5px solid ${poi.resolved ? '#1f2937' : '#111827'}`,
          }}
        />
      </div>
    </div>
  )
}